import Link from 'next/link'
import { and, desc, eq, isNull } from 'drizzle-orm'
import { db } from '@/db'
import { aiArtifacts, knowledgeEntries } from '@/db/schema'
import { requireExpertUser } from '@/lib/role-guard'
import KnowledgeForm from './KnowledgeForm'
import KnowledgeSearch from './KnowledgeSearch'
import ProposalReviewPanel, { type KnowledgeProposal } from './ProposalReviewPanel'

function formatDate(value: Date): string {
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(value)
}

export default async function KnowledgePage({
  searchParams,
}: {
  searchParams: Promise<{ entry?: string }>
}) {
  const expert = await requireExpertUser()
  const selectedEntryId = (await searchParams).entry
  const entries = await db
    .select({
      id: knowledgeEntries.id,
      title: knowledgeEntries.title,
      slug: knowledgeEntries.slug,
      kind: knowledgeEntries.kind,
      tags: knowledgeEntries.tags,
      embeddingUpdatedAt: knowledgeEntries.embeddingUpdatedAt,
      updatedAt: knowledgeEntries.updatedAt,
    })
    .from(knowledgeEntries)
    .where(eq(knowledgeEntries.tenantId, expert.tenantId))
    .orderBy(desc(knowledgeEntries.updatedAt))

  const selectedEntry = selectedEntryId
    ? await db
        .select({
          id: knowledgeEntries.id,
          title: knowledgeEntries.title,
          slug: knowledgeEntries.slug,
          kind: knowledgeEntries.kind,
          tags: knowledgeEntries.tags,
          bodyMarkdown: knowledgeEntries.bodyMarkdown,
        })
        .from(knowledgeEntries)
        .where(
          and(
            eq(knowledgeEntries.id, selectedEntryId),
            eq(knowledgeEntries.tenantId, expert.tenantId),
            eq(knowledgeEntries.isDeleted, false),
          ),
        )
        .limit(1)
        .then((rows) => rows[0] ?? null)
    : null

  const proposalRows = await db
    .select({
      id: aiArtifacts.id,
      contentJson: aiArtifacts.contentJson,
      createdAt: aiArtifacts.createdAt,
    })
    .from(aiArtifacts)
    .where(
      and(
        eq(aiArtifacts.tenantId, expert.tenantId),
        eq(aiArtifacts.artifactType, 'kb_update_proposal'),
        isNull(aiArtifacts.expertAccepted),
      ),
    )
    .orderBy(desc(aiArtifacts.createdAt))

  const proposals: KnowledgeProposal[] = proposalRows
    .map((proposal) => {
      if (!proposal.contentJson) return null
      const parsed = JSON.parse(proposal.contentJson) as {
        title?: string
        slug?: string
        body_markdown?: string
      }
      if (!parsed.title || !parsed.slug || !parsed.body_markdown) return null
      return {
        id: proposal.id,
        title: parsed.title,
        slug: parsed.slug,
        bodyMarkdown: parsed.body_markdown,
        createdAt: proposal.createdAt.toISOString(),
      }
    })
    .filter((proposal): proposal is KnowledgeProposal => proposal !== null)

  return (
    <main className="min-h-screen bg-zinc-50 px-4 py-10 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="mx-auto max-w-[100rem] space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-950 dark:text-white">Knowledge Base</h1>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              Search, author, and review verified operational knowledge.
            </p>
          </div>
          <span className="rounded border border-zinc-200 bg-white px-3 py-1 text-sm text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300">
            {entries.length} entries
          </span>
        </div>

        <KnowledgeSearch />

        <ProposalReviewPanel proposals={proposals} />

        <section className="grid gap-5 lg:grid-cols-[20%_minmax(0,80%)]">
          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            {entries.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <h2 className="text-sm font-medium text-zinc-950 dark:text-white">No entries yet</h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Create the first verified markdown entry.</p>
              </div>
            ) : (
              <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {entries.map((entry) => (
                  <li key={entry.id}>
                    <Link
                      href={`/knowledge?entry=${entry.id}`}
                      className={`block px-4 py-3 transition hover:bg-zinc-100 dark:hover:bg-zinc-800/70 ${
                        selectedEntry?.id === entry.id ? 'bg-zinc-100 dark:bg-zinc-800/70' : ''
                      }`}
                    >
                      <h2 className="text-sm font-semibold leading-5 text-zinc-950 dark:text-white">
                        {entry.title}{' '}
                        <span className="inline-flex align-baseline rounded border border-zinc-200 px-1.5 py-0 text-[11px] font-normal text-zinc-600 dark:border-zinc-700 dark:text-zinc-300">
                          {entry.kind}
                        </span>
                        {entry.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="ml-1 inline-flex align-baseline rounded bg-zinc-100 px-1.5 py-0 text-[11px] font-normal text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
                            {tag}
                          </span>
                        ))}
                        {entry.tags.length > 3 ? (
                          <span className="ml-1 inline-flex align-baseline rounded bg-zinc-100 px-1.5 py-0 text-[11px] font-normal text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                            +{entry.tags.length - 3}
                          </span>
                        ) : null}
                      </h2>
                      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-zinc-600 dark:text-zinc-400">
                        <p className="flex min-w-0 items-center gap-1.5">
                          <span aria-hidden="true" title="Updated">◷</span>
                          <span>{formatDate(entry.updatedAt)}</span>
                        </p>
                        {!entry.embeddingUpdatedAt ? (
                          <span className="shrink-0 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-400/10 dark:text-amber-200">
                            queued
                          </span>
                        ) : null}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">
                {selectedEntry ? 'Edit Entry' : 'Create Entry'}
              </h2>
              {selectedEntry ? (
                <Link href="/knowledge" className="text-xs font-medium text-zinc-500 hover:text-zinc-950 dark:text-zinc-400 dark:hover:text-white">
                  New entry
                </Link>
              ) : null}
            </div>
            <KnowledgeForm key={selectedEntry?.id ?? 'new-entry'} entry={selectedEntry ?? undefined} />
          </div>
        </section>
      </div>
    </main>
  )
}
